<script setup lang="ts">
import MegaAbout from "./megas/MegaAbout.vue";
import MegaCatalogue from "./megas/MegaCatalogue.vue";
import MegaProducts from "./megas/MegaProducts.vue";
import MegaMedia from "./megas/MegaMedia.vue";
import { onMounted, onBeforeUnmount, nextTick, ref } from 'vue';

import { vw, vwWithMax } from '~/utility/utility';

const router = useRouter()


const { $io } = useNuxtApp()

const headerFixed = computed(() => $io.headerFixed);

/**
 * We replicate the JS logic from the original “globalNavDropdowns” example,
 * but in a simplified manner. We do direct DOM queries in `onMounted`.
 * For a more "Vue" approach, you might store each dropdown's open state
 * in a reactive variable, but this reproduces the same behavior directly.
 */



let activeDropdownEl: HTMLElement | null = null;
let closeDropdownTimeout: number | null = null;
let isCompact = false;
let noDropdownTransition = true;
const globalNavRef = ref<HTMLElement | null>(null);
const isUnmounting = ref(false);

// Explicit function defining mega widths

// THESE VALUES ARE ALSO SET IN EACH MEGA MENU COMPONENT, as the default width, 
// IF WE DONT SET IT FOR EACH MEGA COMPONENT THE HEIGHT ISN"T CALCULATED CORRECTLY

function getMegaWidth(dropdownName: string) {
  if(dropdownName === 'about') {
    return vwWithMax(80, 1300);
  }
  if(dropdownName === 'catalogue') {
    return vwWithMax(75, 1300);
  }
  if(dropdownName === 'products') {
    return vwWithMax(80, 1300);
  }
  if(dropdownName === 'media') {
    return vwWithMax(80, 1300);
  }
  return vwWithMax(60, 1200);
}


function checkCollision() {
  if (!globalNavRef.value) return;

  const container = globalNavRef.value;
  const primaryNav = container.querySelector('.navSection.primary') as HTMLElement;
  const secondaryNavItem = container.querySelector('.navSection.secondary .rootLink:first-child') as HTMLElement;
  const primaryNavItem = container.querySelector('.navSection.primary .rootLink:last-child') as HTMLElement;

  if (!primaryNavItem || !secondaryNavItem) return;

  if (window.innerWidth < 670) {
    // We skip collision checks if on mobile
    return;
  }

  // If we are "compact," check if we can un-compact
  if (isCompact) {
    const t = document.body.clientWidth;
    const r = primaryNav.getBoundingClientRect();
    if (r.left + r.width / 2 <= t / 2) {
      container.classList.remove('compact');
      isCompact = false;
    }
  } else {
    // If not compact, check if we must become compact
    const rRect = primaryNavItem.getBoundingClientRect();
    const sRect = secondaryNavItem.getBoundingClientRect();
    if (rRect.right > sRect.left) {
      container.classList.add('compact');
      isCompact = true;
    }
  }
}

async function openDropdown(el: HTMLElement) {
  if (!globalNavRef.value) return;
  const container = globalNavRef.value;
  if (activeDropdownEl === el) return;
  container.classList.add('overlayActive', 'dropdownActive');
  activeDropdownEl = el;

  // Mark this rootLink active, remove from others
  const allRoots = Array.from(container.querySelectorAll('.hasDropdown'));
  allRoots.forEach(r => r.classList.remove('active'));
  el.classList.add('active');

  // Find the matching data-dropdown section
  const dropdownName = el.getAttribute('data-dropdown') as string;
  const sections = container.querySelectorAll('.dropdownSection');
  let newWidth = 520;
  let newHeight = dropdownHeights.value[dropdownName] || 600; // Use precomputed or default height
  let contentEl: HTMLElement | null = null;

  // Mark the active section
  sections.forEach(sec => {
    sec.classList.remove('active', 'left', 'right');
    if (sec.getAttribute('data-dropdown') === dropdownName) {
      sec.classList.add('active');
      const c = sec.querySelector(`#mega-${dropdownName}`) as HTMLElement;
      if (c) {
        contentEl = c;
      }
    } else {
      // We'll guess left or right. This example used left for first, right for others:
      sec.classList.add('right');
    }
  });

  // First-time open? Force a post-paint measurement
  if (!measuredDropdowns.value.has(dropdownName)) {
    ensureObserved(dropdownName);
    const h = await forceMeasure(dropdownName);
    if (h && h > 0) {
      newHeight = h;
      console.log(`First-time measurement for ${dropdownName}:`, h);
    }
  } else {
    // Already known – still sanity update quickly
    const h = measureDropdownHeight(dropdownName);
    if (h && h > 0) {
      newHeight = h;
    }
  }

  // Adjust BG transforms
  const dropdownBG = container.querySelector('.dropdownBackground') as HTMLElement;
  // const altBG = container.querySelector('.alternateBackground') as HTMLElement;
  const arrow = container.querySelector('.dropdownArrow') as HTMLElement;
  const dropContainer = container.querySelector('.dropdownContainer') as HTMLElement;

  newWidth = getMegaWidth(dropdownName as string);
 

  let f = newWidth / 520;
  let l = newHeight / 400;
  const rect = el.getBoundingClientRect();
  let xPos = Math.round(Math.max(rect.left + rect.width / 2 - newWidth / 2, 10));
  // console.log('xPos', xPos)

  // We remove "noDropdownTransition" after a tick
  setTimeout(() => {
    noDropdownTransition = false;
    container.classList.remove('noDropdownTransition');
  }, 50);

  //console.log("vw(100)", vw(100))
  //console.log(`getMegaWidth(${dropdownName})`, getMegaWidth(dropdownName as string))
  
  // offset from center item, items to the left are negative, items to the right are positive (solutions is the center item in this example)
  const offsetFromCenterItem = 50;

  switch(dropdownName) {
    case 'about':
      xPos = ((vw(100) - getMegaWidth(dropdownName as string)) / 2) - offsetFromCenterItem;
      //console.log('xPos', xPos)
      break;
    case 'catalogue':
    xPos = ((vw(100) - getMegaWidth(dropdownName as string)) / 2) - 0;
    //console.log('xPos', xPos)
      break;
    case 'products':
    xPos = ((vw(100) - getMegaWidth(dropdownName as string)) / 2) + offsetFromCenterItem;
    //console.log('xPos', xPos)
      break;
    case 'media':
    xPos = ((vw(100) - getMegaWidth(dropdownName as string)) / 2) + offsetFromCenterItem;
    //console.log('xPos', xPos)
      break;
  }

  dropdownBG.style.transform = `translateX(${xPos}px) scaleX(${f}) scaleY(${l})`;
  dropContainer.style.transform = `translateX(${xPos}px)`;
  dropContainer.style.width = newWidth + 'px';
  dropContainer.style.height = newHeight + 'px';

  //console.log('dropContainer.style.height', newHeight + 'px')

  // arrow
  let arrowX = Math.round(rect.left + rect.width / 2);
  arrow.style.transform = `translateX(${arrowX}px) rotate(45deg)`;

    // alt background
    //   if (contentEl) {
    //     let d = contentEl.children[0]?.clientHeight ?? 0;
    //     d = d / l;
    //     altBG.style.transform = `translateY(${d}px)`;
    //   }

}

function closeDropdown() {
  if (!globalNavRef.value) return;
  const container = globalNavRef.value;
  if (!activeDropdownEl) return;
  // remove active from all root links
  const allRoots = Array.from(container.querySelectorAll('.hasDropdown'));
  allRoots.forEach(r => r.classList.remove('active'));

  container.classList.add('noDropdownTransition');
  noDropdownTransition = true;

  container.classList.remove('overlayActive', 'dropdownActive');
  activeDropdownEl = null;
}

function toggleDropdown(el: HTMLElement) {
  if (activeDropdownEl === el) {
    closeDropdown();
  } else {
    openDropdown(el);
  }
}

// We mimic the "hover" close with timeouts
function startCloseTimeout() {
  closeDropdownTimeout = window.setTimeout(() => {
    closeDropdown();
  }, 50);
}
function stopCloseTimeout() {
  if (closeDropdownTimeout) {
    clearTimeout(closeDropdownTimeout);
    closeDropdownTimeout = null;
  }
}

onMounted(() => {
  checkCollision();
  window.addEventListener('resize', checkCollision);

  // We attach click and mouse events to replicate the original code
  const container = globalNavRef.value;
  if (!container) return;

  const hasDropdowns = Array.from(container.querySelectorAll('.hasDropdown'));
  hasDropdowns.forEach(el => {
    // For mouse click
    el.addEventListener('click', (ev: Event) => {
      ev.preventDefault();
      ev.stopPropagation();
      toggleDropdown(el as HTMLElement);
    });
    // For hover
    el.addEventListener('mouseenter', (ev: any) => {
      if (ev.pointerType === 'touch') return;
      stopCloseTimeout();
      openDropdown(el as HTMLElement);
      const dropdownName = el.getAttribute('data-dropdown');
      const dropdownBG = container.querySelector('.dropdownBackground') as HTMLElement;
      const dropdownContainer = container.querySelector('.dropdownContainer') as HTMLElement;
      if (dropdownName) {
        dropdownContainer.classList.add(dropdownName);
        dropdownBG.classList.add(dropdownName);
      }
    });
    el.addEventListener('mouseleave', (ev: any) => {
      if (ev.pointerType === 'touch') return;
      startCloseTimeout();
      const dropdownName = el.getAttribute('data-dropdown');
      const dropdownBG = container.querySelector('.dropdownBackground') as HTMLElement;
      const dropdownContainer = container.querySelector('.dropdownContainer') as HTMLElement;
      if (dropdownName) {
        dropdownContainer.classList.remove(dropdownName);
        dropdownBG.classList.remove(dropdownName);
      }
    });
  });

  const dropdownContainer = container.querySelector('.dropdownContainer');
  if (dropdownContainer) {
    dropdownContainer.addEventListener('click', ev => {
      ev.stopPropagation();
    });
    dropdownContainer.addEventListener('mouseenter', (ev: any) => {
      if (ev.pointerType === 'touch') return;
      stopCloseTimeout();
    });
    dropdownContainer.addEventListener('mouseleave', (ev: any) => {
      if (ev.pointerType === 'touch') return;
      startCloseTimeout();
    });
  }

  // Close dropdown if click outside
  document.body.addEventListener('click', () => {
    closeDropdown();
  });
});

function redirect(path: string) {
	if(path) {
		router.push(path)
	}
}


const dropdownHeights = ref<{ [key: string]: number }>({
  about: 520, // Updated default height
  solutions: 700,
  products: 750,
  media: 720
});

// Track which dropdowns have been measured
const measuredDropdowns = ref<Set<string>>(new Set());

// Function to measure and cache dropdown height
const measureDropdownHeight = (dropdownName: string): number | null => {
  const element = document.querySelector(`#mega-${dropdownName}`) as HTMLElement;
  if (element) {
    const actualHeight = element.offsetHeight;
    if (actualHeight > 0) {
      dropdownHeights.value[dropdownName] = actualHeight;
      measuredDropdowns.value.add(dropdownName);
      console.log(`Measured ${dropdownName} dropdown height:`, actualHeight);
      return actualHeight;
    }
  }
  return null;
};

// Ensure we only read after layout is flushed (first time)
const forceMeasure = (dropdownName: string): Promise<number | null> => {
  return new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        resolve(measureDropdownHeight(dropdownName));
      });
    });
  });
};

// ResizeObserver to watch for content changes
let ro: ResizeObserver | null = null;
const observed = new Set<HTMLElement>();

const ensureObserved = (name: string) => {
  const el = document.querySelector<HTMLElement>(`#mega-${name}`);
  if (!el || observed.has(el)) return;
  if (!ro) {
    ro = new ResizeObserver(() => {
      // Any observed panel changes size – update cache for whichever it is
      for (const n of Object.keys(dropdownHeights.value)) {
        const pane = document.querySelector<HTMLElement>(`#mega-${n}`);
        if (pane) {
          const newHeight = pane.offsetHeight;
          if (newHeight > 0) {
            dropdownHeights.value[n] = newHeight;
            console.log(`ResizeObserver updated ${n} height:`, newHeight);
          }
        }
      }
    });
  }
  ro.observe(el);
  observed.add(el);
};

onMounted(() => {
	// Delay initial measurement to ensure components are rendered
	setTimeout(() => {
		const keys = Object.keys(dropdownHeights.value);
		keys.forEach(key => {
			ensureObserved(key);
			measureDropdownHeight(key);
		});
	}, 100);
});

// Clean up to prevent memory leaks and timing issues
onBeforeUnmount(() => {
	isUnmounting.value = true
	
	// Clear any pending timeouts
	if (closeDropdownTimeout) {
		clearTimeout(closeDropdownTimeout)
		closeDropdownTimeout = null
	}
	
	// Disconnect ResizeObserver
	if (ro) {
		ro.disconnect();
		ro = null;
	}
	
	// Reset all state variables
	activeDropdownEl = null
	isCompact = false
	noDropdownTransition = true
	
	// Remove event listeners if globalNavRef exists
	if (globalNavRef.value) {
		// Clean up any event listeners that might have been added
		const container = globalNavRef.value
		container.classList.remove('overlayActive', 'dropdownActive', 'compact')
	}
});
</script>

<template>
  <header
    ref="globalNavRef"
    class="globalNav noDropdownTransition"
    :class="{'header-fixed': headerFixed}"
    @click.stop
  >
    <div class="container-lg">
      <ul class="navRoot">

        <!-- Removed the navSection.logo and navSection.secondary entirely -->

        <!-- Primary nav: Products / Developers / Company -->
        <li class="navSection primary sec-primary-menu">
            <a @click="redirect('/')" class="nav-item rootLink item-home colorize hidden! dm:flex!"><span>Home</span></a>
            <a class="nav-item rootLink item-about colorize hasDropdown" data-dropdown="about"><span>About us</span></a>
            <a class="nav-item rootLink item-catalogue hasDropdown colorize" data-dropdown="catalogue"><span>Catalogue</span></a>
            <a class="nav-item rootLink item-services colorize" @click="redirect('/services')"><span>Services</span></a>
            <!-- <a class="nav-item rootLink item-solutions hasDropdown colorize" data-dropdown="categories"><span>Contact us</span></a> -->
            <a class="nav-item rootLink item-products colorize" @click="redirect('/products')"><span>Products</span></a>
            <!-- <a class="nav-item rootLink item-media hasDropdown colorize" data-dropdown="news"><span>News</span></a> -->
						<!--
						<a class="nav-item rootLink item-products hasDropdown colorize" data-dropdown="products"><span>Products</span></a>
            <a class="nav-item rootLink item-media hasDropdown colorize" data-dropdown="media"><span>Media</span></a>
						-->
        </li>

      </ul>
    </div>

    <!-- The dropdown root container / background / arrow  -->
    <div class="dropdownRoot">
      <div class="dropdownBackground">
        <div class="alternateBackground"></div>
      </div>
      <div class="dropdownArrow"></div>
      <div class="dropdownContainer">
        <!-- Products dropdown -->
        <div class="dropdownSection about active" data-dropdown="about">
          <div class="dropdownContent">
            <MegaAbout /> 
          </div>
        </div>

        <!-- Developers dropdown -->
        <div class="dropdownSection catalogue right" data-dropdown="catalogue">
          <div class="dropdownContent">
            <MegaCatalogue />
          </div>
        </div>

        <!-- Company dropdown -->
        <!-- <div class="dropdownSection products right" data-dropdown="products">
          <div class="dropdownContent">
            <MegaProducts />
         
          </div>
        </div>
         -->
        <div class="dropdownSection media right" data-dropdown="media">
          <div class="dropdownContent">
            <MegaMedia />
          </div>
        </div>
      </div>
    </div>
  </header>
</template>



<style scoped lang="scss">
/* === Copied SCSS (minus brand, sign-in sections) === */
/* All the content from your "Here is the css (scss)" part: */


// .dropdownContainer.about {
//   width: 80% !important;
// }

// .dropdownContainer.solutions {
//   width: 60% !important;
// }
.dropdownBackground.about {
    @include mq($from: ds) {
        max-height: 670px;
    }
}
.dropdownContainer.about {
    @include mq($from: ds) {
        max-height: 670px;
    }
}

// Safeguard to prevent first paint stretching in absolute positioned content
.dropdownContent {
  min-height: 0 !important;
  max-height: none !important;
}

.globalNav {
    position: absolute;
    left: 0;
    top: 10px;
    right: 0;
    z-index: 1;
    height: 50px;
    -webkit-perspective: 2000px;
    perspective: 2000px
}
.globalNav ul {
    padding: 0;
    margin: 0
}
.globalNav li {
    list-style: none
}
.globalNav a {
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;
    color: #6772e5;
    -webkit-transition: color .1s;
    transition: color .1s
}
.globalNav a:hover {
    color: #32325d
}
.globalNav>.container-lg {
    padding: 0
}
.globalNav .navRoot {
    position: relative
}
.globalNav .navSection.logo {
    position: absolute;
    top: 0;
    left: 0
}
.globalNav .navSection.primary, .globalNav .navSection.secondary {
    display: none
}
@media (min-width:670px) {
    .globalNav .navSection.primary, .globalNav .navSection.secondary {
        display: -webkit-box;
        display: -webkit-flex;
        display: -ms-flexbox;
        display: flex
    }
}
.globalNav .navSection.primary {
    -webkit-box-pack: center;
    -webkit-justify-content: center;
    -ms-flex-pack: center;
    justify-content: center
}
.globalNav .navSection.secondary {
    position: absolute;
    top: 0;
    right: 0
}
.globalNav .navSection.mobile {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-pack: end;
    -webkit-justify-content: flex-end;
    -ms-flex-pack: end;
    justify-content: flex-end
}
@media (min-width:670px) {
    .globalNav .navSection.mobile {
        display: none
    }
}
.globalNav.compact .navRoot {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex
}
.globalNav.compact .navSection.logo {
    position: static;
    -webkit-box-flex: 1;
    -webkit-flex-grow: 1;
    -ms-flex-positive: 1;
    flex-grow: 1
}
.globalNav.compact .navSection.secondary {
    position: static
}
.globalNav .rootLink {
    display: inline-block;
    height: 50px;
    white-space: nowrap;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    font-size: 17px;
    line-height: 50px;
    font-weight: 400;
    margin: 0;
    padding: 0 10px
}
@media (min-width:670px) {
    .globalNav .rootLink {
        padding: 0 calc(10px + (100vw - 670px) * .040541)
    }
}
@media (min-width:1040px) {
    .globalNav .rootLink {
        padding: 0 25px
    }
}
.globalNav .navSection.logo .rootLink {
    padding-left: 20px!important
}
.globalNav .navSection.secondary .rootLink:last-child {
    padding-right: 20px!important
}
.globalNav .navSection.primary .rootLink {
    font-weight: 500
}
.globalNav .colorize {
    color: #6772e5;
    -webkit-transition: color .1s ease;
    transition: color .1s ease
}
.globalNav .colorize.active, .globalNav .colorize:hover {
    color: #32325d
}
.globalNav .hasDropdown {
    cursor: default
}
.globalNav .item-home h1 {
    line-height: 50px;
    font-size: 20px;
    margin: 0;
    color: inherit
}
.globalNav .item-home svg {
    vertical-align: -5px
}
.globalNav .item-home svg path {
    fill: currentColor
}
.globalNav .item-dashboard:after {
    font: normal 16px StripeIcons;
    content: '\279E';
    padding-left: 1px
}
.globalNav .dropdownRoot {
    position: absolute;
    z-index: 1000;
    left: 0;
    right: 0;
    top: 50px;
    pointer-events: none;
    -webkit-transform: rotateX(-15deg);
    transform: rotateX(-15deg);
    -webkit-transform-origin: 50% -50px;
    transform-origin: 50% -50px;
    opacity: 0;
    will-change: transform, opacity;
    -webkit-transition-property: opacity, -webkit-transform;
    transition-property: opacity, -webkit-transform;
    transition-property: transform, opacity;
    transition-property: transform, opacity, -webkit-transform;
    -webkit-transition-duration: .25s;
    transition-duration: .25s;
    display: none
}
@media (min-width:670px) {
    .globalNav .dropdownRoot {
        display: block
    }
}
.globalNav.dropdownActive .dropdownRoot {
    opacity: 1;
    pointer-events: auto;
    -webkit-transform: none;
    transform: none
}
.globalNav .dropdownBackground {
    // background: #fff;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 50px 100px rgba(50, 50, 93, .1), 0 15px 35px rgba(50, 50, 93, .15), 0 5px 15px rgba(0, 0, 0, .1);
    width: 520px;
    height: 400px;
    -webkit-transform: translateX(0);
    transform: translateX(0);
    -webkit-transform-origin: 0 0;
    transform-origin: 0 0
}
.globalNav .alternateBackground, .globalNav .dropdownBackground {
    position: absolute;
    top: 0;
    left: 0;
    will-change: transform;
    -webkit-transition-property: -webkit-transform;
    transition-property: -webkit-transform;
    transition-property: transform;
    transition-property: transform, -webkit-transform;
    -webkit-transition-duration: .25s;
    transition-duration: .25s
}
.globalNav .alternateBackground {
    right: 0;
    height: 1000px;
    background: #f6f9fc
}
.globalNav .dropdownArrow {
    top: -6px;
    margin: 0 0 0 -6px;
    width: 12px;
    height: 12px;
    -webkit-transform: rotate(45deg);
    transform: rotate(45deg);
    border-radius: 4px 0 0 0;
    background: #fff;
    box-shadow: -3px -3px 5px rgba(82, 95, 127, .04);
    will-change: transform;
    -webkit-transition-property: -webkit-transform;
    transition-property: -webkit-transform;
    transition-property: transform;
    transition-property: transform, -webkit-transform
}
.globalNav .dropdownArrow, .globalNav .dropdownContainer {
    position: absolute;
    left: 0;
    -webkit-transition-duration: .25s;
    transition-duration: .25s
}
.globalNav .dropdownContainer {
    overflow: hidden;
    top: 0;
    -webkit-transform: translateX(0);
    transform: translateX(0);
    will-change: transform, width, height;
    -webkit-transition-property: width, height, -webkit-transform;
    transition-property: width, height, -webkit-transform;
    transition-property: transform, width, height;
    transition-property: transform, width, height, -webkit-transform;
		border-radius: 8px;
		background: white;
}
.globalNav .dropdownSection {
    opacity: 0;
    pointer-events: none;
    will-change: transform, opacity;
    -webkit-transition-property: opacity, -webkit-transform;
    transition-property: opacity, -webkit-transform;
    transition-property: transform, opacity;
    transition-property: transform, opacity, -webkit-transform;
    -webkit-transition-duration: .25s;
    transition-duration: .25s;
}
.globalNav .dropdownSection.active {
    opacity: 1;
    -webkit-transform: translateX(0);
    transform: translateX(0)
}
.globalNav .dropdownSection.left {
    -webkit-transform: translateX(-150px);
    transform: translateX(-150px)
}
.globalNav .dropdownSection.right {
    -webkit-transform: translateX(150px);
    transform: translateX(150px)
}
.globalNav.dropdownActive .dropdownSection.active {
    pointer-events: auto
}
.globalNav.noDropdownTransition .alternateBackground, .globalNav.noDropdownTransition .dropdownArrow, .globalNav.noDropdownTransition .dropdownBackground, .globalNav.noDropdownTransition .dropdownContainer, .globalNav.noDropdownTransition .dropdownSection {
    -webkit-transition: none;
    transition: none
}
.globalNav .dropdownContent {
    position: absolute;
    top: 0;
    left: 0;
		width:100%;
}
.globalNav .linkGroup {
    padding: 20px 35px
}
.globalNav .linkContainer {
    display: block;
    padding: 9px 0
}
.globalNav .linkTitle {
    margin: 0;
    color: #6772e5;
    font-size: 16px;
    line-height: 22px;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: .025em
}
.globalNav .linkSub {
    font-size: 15px;
    line-height: 22px;
    color: #6b7c93;
    margin: 5px 0 0;
    display: block
}
.globalNav .linkSub, .globalNav .linkTitle {
    -webkit-transition: color .1s;
    transition: color .1s
}
.globalNav .linkContainer:hover .linkTitle, .globalNav .linkTitle:hover {
    color: #32325d
}
.globalNav .linkContainer:hover .linkSub {
    color: #424770
}
.globalNav .hover-fillDark, .globalNav .hover-fillLight {
    -webkit-transition: fill .1s;
    transition: fill .1s
}
.globalNav .hover-strokeDark, .globalNav .hover-strokeLight {
    -webkit-transition: stroke .1s;
    transition: stroke .1s
}
.globalNav .linkContainer:hover .hover-fillLight {
    fill: #8898aa
}
.globalNav .linkContainer:hover .hover-fillDark {
    fill: #32325d
}
.globalNav .linkContainer:hover .hover-strokeLight {
    stroke: #8898aa
}
.globalNav .linkContainer:hover .hover-strokeDark {
    stroke: #32325d
}
.globalNav .linkIcon {
    white-space: nowrap
}
.globalNav .linkIcon svg {
    margin: 0 12px -3px -1px
}
.globalNav .withIcon {
    padding-left: 28px
}
.globalNav .withIcon .linkTitle {
    margin-left: -28px
}
.globalNav .new-badge {
    display: inline-block;
    margin-left: 5px;
    vertical-align: 2px;
    color: #fff;
    text-transform: uppercase;
    font-size: 10px;
    line-height: 14px;
    font-weight: 700;
    background: #6772e5;
    -webkit-transition: background .15s;
    transition: background .15s;
    border-radius: 10px;
    padding: 0 5px;
    height: auto;
    top: auto;
    box-shadow: none
}
.globalNav .linkContainer:hover .new-badge {
    background: #32325d
}
.globalNav .productsGroup {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-flex-wrap: wrap;
    -ms-flex-wrap: wrap;
    flex-wrap: wrap;
    width: 640px;
    max-width: calc(100vw - 85px);
    margin: -5px -10px
}
.globalNav .productsGroup>li {
    -webkit-flex: 1 0 50%;
    -ms-flex: 1 0 50%;
    flex: 1 0 50%
}
.globalNav .productsGroup .linkContainer, .globalNav .productsGroup>li {
    -webkit-box-flex: 1;
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex
}
.globalNav .productsGroup .linkContainer {
    padding: 18px 10px;
    -webkit-flex: 1;
    -ms-flex: 1;
    flex: 1;
    -webkit-box-align: center;
    -webkit-align-items: center;
    -ms-flex-align: center;
    align-items: center
}
.globalNav .productsGroup svg {
    width: 48px;
    height: 48px
}
.globalNav .productLinkContent {
    -webkit-box-flex: 1;
    -webkit-flex: 1;
    -ms-flex: 1;
    flex: 1;
    margin-left: 20px;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -webkit-flex-direction: column;
    -ms-flex-direction: column;
    flex-direction: column;
    min-height: 71px
}
.globalNav .prodsubGroup, .globalNav .productLinkContent {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-pack: center;
    -webkit-justify-content: center;
    -ms-flex-pack: center;
    justify-content: center
}
.globalNav .prodsubGroup .linkContainer {
    display: -webkit-inline-box;
    display: -webkit-inline-flex;
    display: -ms-inline-flexbox;
    display: inline-flex;
    -webkit-box-orient: horizontal;
    -webkit-box-direction: normal;
    -webkit-flex-direction: row;
    -ms-flex-direction: row;
    flex-direction: row;
    padding: 13px 30px
}
.globalNav .prodsubGroup .linkSub {
    margin: 0 0 0 10px
}
.globalNav .item-subscriptions .linkTitle {
    color: #24b47e
}
.globalNav .item-connect .linkTitle {
    color: #3297d3
}
.globalNav .item-relay .linkTitle {
    color: #e25950
}
.globalNav .item-atlas .linkTitle {
    color: #e39f48
}
.globalNav .item-radar .linkTitle {
    color: #b76ac4
}
.globalNav .item-radar .new-badge {
    background: #b76ac4
}
.globalNav .documentationGroup .linkSub {
    max-width: 250px
}
.globalNav .documentationArticles {
    font-size: 15px;
    line-height: 26px;
    margin: 15px 20px 10px 28px;
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    white-space: nowrap
}
.globalNav .documentationArticles>ul {
    margin-right: 40px
}
.globalNav .documentationArticles>ul:last-child {
    margin-right: 0
}
.globalNav .documentationArticles h4 {
    font-size: 14px;
    line-height: 22px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: .025em;
    margin: 0 0 3px;
    color: #8898aa
}
.globalNav .blogPosts {
    margin: 5px 10px 5px 28px
}
.globalNav .blogPosts a {
    display: block;
    white-space: nowrap;
    padding: 5px 0
}
.globalNav .blogPosts a:after {
    content: '\27A2';
    font: normal 16px StripeIcons;
    margin-left: 6px;
    vertical-align: -3px
}
.globalNav .blogPosts .title {
    font-size: 15px;
    line-height: 22px;
    display: inline-block;
    white-space: nowrap;
    max-width: 300px;
    text-overflow: ellipsis;
    overflow: hidden;
    vertical-align: top
}
.globalNav .blogPosts .title.new {
    font-weight: 500
}
.globalNav .blogPosts .new-badge {
    vertical-align: -1px
}
.globalNav .blogPosts a:hover .new-badge {
    background: #32325d
}
.globalNav .navSection.mobile .rootLink {
    cursor: pointer;
    width: 50px;
    height: 50px;
    position: relative
}
.globalNav .navSection.mobile .rootLink h2 {
    color: inherit
}
.globalNav .navSection.mobile .rootLink h2, .globalNav .navSection.mobile .rootLink h2:after, .globalNav .navSection.mobile .rootLink h2:before {
    position: absolute;
    width: 24px;
    height: 3px;
    border-radius: 1px;
    background: currentColor
}
.globalNav .navSection.mobile .rootLink h2 {
    font-size: 0;
    left: 13px;
    top: 23px
}
.globalNav .navSection.mobile .rootLink h2:after, .globalNav .navSection.mobile .rootLink h2:before {
    content: '';
    left: 0
}
.globalNav .navSection.mobile .rootLink h2:before {
    top: -9px
}
.globalNav .navSection.mobile .rootLink h2:after {
    top: 9px
}
.globalNav .popup {
    position: absolute;
    left: 10px;
    top: 5px;
    right: 10px;
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-pack: end;
    -webkit-justify-content: flex-end;
    -ms-flex-pack: end;
    justify-content: flex-end;
    pointer-events: none;
    -webkit-perspective: 2000px;
    perspective: 2000px
}
.globalNav .popupContainer {
    background: #fff;
    box-shadow: 0 50px 100px rgba(50, 50, 93, .1), 0 15px 35px rgba(50, 50, 93, .15), 0 5px 15px rgba(0, 0, 0, .1);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
    font-size: 17px;
    line-height: 40px;
    white-space: nowrap;
    -webkit-box-flex: 1;
    -webkit-flex-grow: 1;
    -ms-flex-positive: 1;
    flex-grow: 1;
    -webkit-transform: rotate3d(1, 1, 0, -15deg);
    transform: rotate3d(1, 1, 0, -15deg);
    -webkit-transform-origin: 100% 0;
    transform-origin: 100% 0;
    opacity: 0;
    will-change: transform, opacity;
    -webkit-transition-property: opacity, -webkit-transform;
    transition-property: opacity, -webkit-transform;
    transition-property: transform, opacity;
    transition-property: transform, opacity, -webkit-transform;
    -webkit-transition-duration: .25s;
    transition-duration: .25s
}
.globalNav .navSection.mobile.globalPopupActive .popupContainer {
    -webkit-transform: none;
    transform: none;
    opacity: 1;
    pointer-events: auto
}
.globalNav .popup a {
    display: block
}
.globalNav .popupCloseButton {
    position: absolute;
    right: 0;
    top: 0;
    width: 51px;
    height: 51px;
    font-size: 0;
    cursor: pointer
}
.globalNav .popupCloseButton:after, .globalNav .popupCloseButton:before {
    content: '';
    position: absolute;
    background: #6772e5;
    border-radius: 1px;
    left: 14px;
    right: 14px;
    top: 24px;
    height: 3px;
    -webkit-transform: rotate(45deg);
    transform: rotate(45deg);
    -webkit-transition: background .1s;
    transition: background .1s
}
.globalNav .popupCloseButton:after {
    -webkit-transform: rotate(-45deg);
    transform: rotate(-45deg)
}
.globalNav .popupCloseButton:hover:after, .globalNav .popupCloseButton:hover:before {
    background: #32325d
}
.globalNav .mobileSignIn {
    background: #f6f9fc;
    display: block;
    padding: 12px 30px;
    font-weight: 600
}
.globalNav .mobileSignIn:after {
    font: normal 16px StripeIcons;
    content: '\279C';
    margin-left: 6px
}
.globalNav .mobileProducts {
    padding: 20px 0 15px
}
.globalNav .mobileProducts h4 {
    font-size: 14px;
    font-weight: 600;
    letter-spacing: .025em;
    color: #8898aa;
    text-transform: uppercase;
    margin: -5px 0 0 30px
}
.globalNav .mobileProductsList {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-flex-wrap: wrap;
    -ms-flex-wrap: wrap;
    flex-wrap: wrap
}
.globalNav .mobileProductsList>ul {
    -webkit-box-flex: 1;
    -webkit-flex-grow: 1;
    -ms-flex-positive: 1;
    flex-grow: 1
}
.globalNav .mobileProductsList>ul:last-child {
    -webkit-box-flex: 3;
    -webkit-flex-grow: 3;
    -ms-flex-positive: 3;
    flex-grow: 3
}
.globalNav .mobileProductsList a {
    padding: 0 30px;
    font-size: 20px;
    font-weight: 500;
    line-height: 50px
}
.globalNav .mobileProductsList a:hover {
    color: #32325d
}
.globalNav .mobileProductsList a svg {
    display: inline-block;
    width: 26px;
    height: 26px;
    margin: 0 15px 0 -3px;
    vertical-align: -5px
}
.globalNav .mobileProductsList .item-subscriptions {
    color: #24b47e
}
.globalNav .mobileProductsList .item-connect {
    color: #3297d3
}
.globalNav .mobileProductsList .item-relay {
    color: #e25950
}
.globalNav .mobileProductsList .item-atlas {
    color: #e39f48
}
.globalNav .mobileProductsList .item-radar {
    color: #b76ac4
}
.globalNav .mobileSecondaryNav {
    border-top: 2px solid #f6f9fc;
    padding: 15px 10px;
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-flex-wrap: wrap;
    -ms-flex-wrap: wrap;
    flex-wrap: wrap
}
.globalNav .mobileSecondaryNav>ul {
    -webkit-box-flex: 1;
    -webkit-flex-grow: 1;
    -ms-flex-positive: 1;
    flex-grow: 1
}
.globalNav .mobileSecondaryNav>ul:last-child {
    -webkit-box-flex: 3;
    -webkit-flex-grow: 3;
    -ms-flex-positive: 3;
    flex-grow: 3
}
.globalNav .mobileSecondaryNav a {
    padding: 0 20px;
    min-width: 100px
}
.globalFooter {
    font-family: Camphor, Open Sans, Segoe UI, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: #f6f9fc;
    position: relative
}
.globalFooter.withCards {
    background: transparent;
    overflow: hidden;
    padding-top: 500px;
    margin-top: -480px;
    pointer-events: none
}
.globalFooter.withCards>* {
    pointer-events: auto;
    position: relative
}
.globalFooter.withCards:before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 600px;
    height: 2000px;
    background: #f6f9fc;
    -webkit-transform: skew(0, -12deg);
    transform: skew(0, -12deg)
}
.globalFooterCards .container-xl {
    -webkit-flex-wrap: wrap;
    -ms-flex-wrap: wrap;
    flex-wrap: wrap;
    margin: -15px auto;
    padding: 0 5px
}
.globalFooterCards .container-xl, a.globalFooterCard, div.globalFooterCard {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex
}
a.globalFooterCard, div.globalFooterCard {
    -webkit-box-flex: 1;
    -webkit-flex: 1 1 100%;
    -ms-flex: 1 1 100%;
    flex: 1 1 100%;
    margin: 15px;
    z-index: 501;
    position: relative;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -webkit-flex-direction: column;
    -ms-flex-direction: column;
    flex-direction: column;
    -webkit-box-pack: center;
    -webkit-justify-content: center;
    -ms-flex-pack: center;
    justify-content: center;
    overflow: hidden;
    padding: 40px 40px 40px 120px;
    background-color: #fff;
    border-radius: 4px;
    box-shadow: 0 15px 35px rgba(50, 50, 93, .1), 0 5px 15px rgba(0, 0, 0, .07);
    -webkit-transition-property: color, background-color, box-shadow, -webkit-transform;
    transition-property: color, background-color, box-shadow, -webkit-transform;
    transition-property: color, background-color, box-shadow, transform;
    transition-property: color, background-color, box-shadow, transform, -webkit-transform;
    -webkit-transition-duration: .15s;
    transition-duration: .15s
}
a.globalFooterCard:after, div.globalFooterCard:after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background: #aab7c4;
    pointer-events: none;
    opacity: 0;
    -webkit-transition: opacity .15s;
    transition: opacity .15s
}
@media (min-width:670px) {
    a.globalFooterCard, div.globalFooterCard {
        -webkit-flex-basis: 1%;
        -ms-flex-preferred-size: 1%;
        flex-basis: 1%;
        padding-left: 100px
    }
}
@media (min-width:880px) {
    a.globalFooterCard, div.globalFooterCard {
        padding-left: 120px
    }
}
a.globalFooterCard:hover, div.globalFooterCard:hover {
    color: #32325d!important;
    -webkit-transform: translateY(-2px);
    transform: translateY(-2px);
    box-shadow: 0 18px 35px rgba(50, 50, 93, .1), 0 8px 15px rgba(0, 0, 0, .07)
}
a.globalFooterCard:active, div.globalFooterCard:active {
    -webkit-transform: translateY(2px);
    transform: translateY(2px);
    box-shadow: 0 11px 20px rgba(50, 50, 93, .1), 0 6px 11px rgba(0, 0, 0, .08)
}
a.globalFooterCard:active:after, div.globalFooterCard:active:after {
    opacity: .15
}
a.globalFooterCard img, a.globalFooterCard svg, div.globalFooterCard img, div.globalFooterCard svg {
    position: absolute;
    width: 130px;
    height: 130px;
    left: -35px;
    top: calc(50% - 65px)
}
@media (min-width:670px) {
    a.globalFooterCard img, a.globalFooterCard svg, div.globalFooterCard img, div.globalFooterCard svg {
        left: -50px
    }
}
@media (min-width:880px) {
    a.globalFooterCard img, a.globalFooterCard svg, div.globalFooterCard img, div.globalFooterCard svg {
        left: -35px
    }
}
a.globalFooterCard svg .hover-fillDark, a.globalFooterCard svg .hover-fillLight, div.globalFooterCard svg .hover-fillDark, div.globalFooterCard svg .hover-fillLight {
    -webkit-transition: fill .15s;
    transition: fill .15s
}
a.globalFooterCard svg .hover-strokeDark, a.globalFooterCard svg .hover-strokeLight, div.globalFooterCard svg .hover-strokeDark, div.globalFooterCard svg .hover-strokeLight {
    -webkit-transition: stroke .15s;
    transition: stroke .15s
}
a.globalFooterCard:hover svg .hover-fillDark, div.globalFooterCard:hover svg .hover-fillDark {
    fill: #32325d
}
a.globalFooterCard:hover svg .hover-fillLight, div.globalFooterCard:hover svg .hover-fillLight {
    fill: #8898aa
}
a.globalFooterCard:hover svg .hover-strokeDark, div.globalFooterCard:hover svg .hover-strokeDark {
    stroke: #32325d
}
a.globalFooterCard:hover svg .hover-strokeLight, div.globalFooterCard:hover svg .hover-strokeLight {
    stroke: #8898aa
}
a.globalFooterCard h2, div.globalFooterCard h2 {
    margin: 0 0 5px;
    white-space: normal
}
a.globalFooterCard p, div.globalFooterCard p {
    margin: 5px 0 0
}
a.globalFooterCard.card-pricing, div.globalFooterCard.card-pricing {
    color: #24b47e
}
a.globalFooterCard.card-documentation, div.globalFooterCard.card-documentation {
    color: #b76ac4
}
a.globalFooterCard.card-subscriptions, div.globalFooterCard.card-subscriptions {
    color: #24b47e
}
a.globalFooterCard.card-connect, div.globalFooterCard.card-connect {
    color: #3297d3
}
a.globalFooterCard.card-relay, div.globalFooterCard.card-relay {
    color: #e25950
}
a.globalFooterCard.card-atlas, div.globalFooterCard.card-atlas {
    color: #e39f48
}
a.globalFooterCard.card-radar, div.globalFooterCard.card-radar {
    color: #b76ac4
}
.globalFooterCTA {
    padding: 50px 0;
    border-bottom: 2px solid rgba(207, 215, 223, .25)
}
@media (min-width:670px) {
    .globalFooterCTA .container-lg {
        display: -webkit-box;
        display: -webkit-flex;
        display: -ms-flexbox;
        display: flex;
        -webkit-box-align: center;
        -webkit-align-items: center;
        -ms-flex-align: center;
        align-items: center
    }
}
.globalFooterCTA .buttons, .globalFooterCTA .content {
    -webkit-box-flex: 1;
    -webkit-flex: 1 0 50%;
    -ms-flex: 1 0 50%;
    flex: 1 0 50%
}
.globalFooterCTA .title {
    font-weight: 400;
    font-size: 30px;
    line-height: 45px;
    color: #32325d;
    margin: .75em 0
}
.globalFooterCTA .subtitle {
    font-weight: 300;
    color: #6772e5;
    display: block
}
@media (min-width:670px) {
    .globalFooterCTA .common-ButtonGroup {
        -webkit-box-pack: end;
        -webkit-justify-content: flex-end;
        -ms-flex-pack: end;
        justify-content: flex-end
    }
}
.globalFooterNav {
    padding: 65px 0 55px;
    color: #8898aa;
    line-height: 30px;
    font-size: 15px;
    white-space: nowrap
}
.globalFooterNav ul {
    padding: 0;
    margin: 0
}
.globalFooterNav li {
    list-style: none
}
.globalFooterNav .container-lg {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-flex-wrap: wrap;
    -ms-flex-wrap: wrap;
    flex-wrap: wrap
}
@media (min-width:880px) {
    .globalFooterNav .container-lg {
        -webkit-flex-wrap: nowrap;
        -ms-flex-wrap: nowrap;
        flex-wrap: nowrap
    }
}
.globalFooterNav a {
    text-decoration: none;
    color: inherit;
    -webkit-transition: color .1s;
    transition: color .1s
}
.globalFooterNav a:hover {
    color: #32325d
}
.globalFooterNav .metaNav {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-orient: horizontal;
    -webkit-box-direction: normal;
    -webkit-flex-direction: row;
    -ms-flex-direction: row;
    flex-direction: row;
    -webkit-box-flex: 2;
    -webkit-flex-grow: 2;
    -ms-flex-positive: 2;
    flex-grow: 2;
    -webkit-flex-basis: 100%;
    -ms-flex-preferred-size: 100%;
    flex-basis: 100%;
    margin-bottom: 20px
}
@media (min-width:880px) {
    .globalFooterNav .metaNav {
        -webkit-flex-basis: auto;
        -ms-flex-preferred-size: auto;
        flex-basis: auto;
        -webkit-box-orient: vertical;
        -webkit-box-direction: normal;
        -webkit-flex-direction: column;
        -ms-flex-direction: column;
        flex-direction: column;
        margin: 0 0 20px
    }
}
.globalFooterNav .metaNav>li {
    margin-right: 20px
}
.globalFooterNav .metaNav .select {
    position: relative;
    -webkit-perspective: 2000px;
    perspective: 2000px;
    z-index: 501
}
.globalFooterNav .metaNav .rootLink {
    color: #6772e5;
    font-weight: 600;
    cursor: pointer
}
.globalFooterNav .metaNav .rootLink:hover {
    color: #32325d
}
.globalFooterNav .metaNav .rootLink svg {
    display: inline-block;
    vertical-align: -1px;
    margin: 0 7px 0 -2px
}
.globalFooterNav .metaNav .rootLink svg path {
    fill: currentColor
}
.globalFooterNav .metaNav .select.globalPopupActive .rootLink {
    color: #32325d
}
@media (min-width:880px) {
    .globalFooterNav .metaNav .country {
        margin-bottom: 5px
    }
}
.globalFooterNav .metaNav .space {
    -webkit-box-flex: 2;
    -webkit-flex-grow: 2;
    -ms-flex-positive: 2;
    flex-grow: 2
}
.globalFooterNav .metaNav .copyright {
    margin-right: 0;
    color: #cfd7df
}
.globalFooterNav .siteNav {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-flex-wrap: wrap;
    -ms-flex-wrap: wrap;
    flex-wrap: wrap;
    -webkit-box-flex: 1;
    -webkit-flex-grow: 1;
    -ms-flex-positive: 1;
    flex-grow: 1;
    -webkit-box-pack: justify;
    -webkit-justify-content: space-between;
    -ms-flex-pack: justify;
    justify-content: space-between
}
@media (min-width:670px) {
    .globalFooterNav .siteNav {
        -webkit-flex-wrap: nowrap;
        -ms-flex-wrap: nowrap;
        flex-wrap: nowrap
    }
}
@media (min-width:880px) {
    .globalFooterNav .siteNav {
        -webkit-box-flex: 0;
        -webkit-flex-grow: 0;
        -ms-flex-positive: 0;
        flex-grow: 0
    }
}
.globalFooterNav .siteNav .column {
    -webkit-flex-basis: 50%;
    -ms-flex-preferred-size: 50%;
    flex-basis: 50%;
    margin-bottom: 20px
}
@media (min-width:670px) {
    .globalFooterNav .siteNav .column {
        -webkit-flex-basis: auto!important;
        -ms-flex-preferred-size: auto!important;
        flex-basis: auto!important;
        margin-right: 40px
    }
}
@media (min-width:880px) {
    .globalFooterNav .siteNav .column {
        margin-right: 30px
    }
}
@media (min-width:1040px) {
    .globalFooterNav .siteNav .column {
        margin-right: 40px
    }
}
.globalFooterNav .siteNav .column:last-child {
    margin-right: 0
}
.globalFooterNav .siteNav .splitColumn {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-flex-wrap: wrap;
    -ms-flex-wrap: wrap;
    flex-wrap: wrap
}
.globalFooterNav .siteNav li {
    margin-right: 40px
}
.globalFooterNav .siteNav .column:last-child li {
    margin-right: 0
}
.globalFooterNav .siteNav h4 {
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .025em;
    margin: 0 0 5px
}
.globalFooterNav .siteNav strong {
    font-weight: 500
}
.globalFooterNav .popup {
    position: absolute;
    bottom: 40px;
    left: -5px;
    z-index: 1000;
    font-size: 15px;
    line-height: 26px;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 50px 100px rgba(50, 50, 93, .1), 0 15px 35px rgba(50, 50, 93, .15), 0 5px 15px rgba(0, 0, 0, .1);
    pointer-events: none;
    -webkit-transform: rotate3d(1, 1, 0, 15deg);
    transform: rotate3d(1, 1, 0, 15deg);
    -webkit-transform-origin: 0 100%;
    transform-origin: 0 100%;
    opacity: 0;
    will-change: transform, opacity;
    -webkit-transition-property: opacity, -webkit-transform;
    transition-property: opacity, -webkit-transform;
    transition-property: transform, opacity;
    transition-property: transform, opacity, -webkit-transform;
    -webkit-transition-duration: .25s;
    transition-duration: .25s
}
@media (min-width:1160px) {
    .globalFooterNav .popup {
        left: -65px
    }
}
.globalFooterNav .popup:before {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 30px;
    width: 20px;
    height: 20px;
    -webkit-transform: rotate(45deg);
    transform: rotate(45deg);
    border-radius: 20px 0 3px 0;
    background: #fff
}
@media (min-width:1160px) {
    .globalFooterNav .popup:before {
        left: 60px
    }
}
.globalFooterNav .globalPopupActive .popup {
    -webkit-transform: none;
    transform: none;
    opacity: 1;
    pointer-events: auto
}
.globalFooterNav .optionList {
    color: #525f7f;
    white-space: nowrap
}
.globalFooterNav .optionList a {
    border-radius: 4px;
    display: block;
    line-height: 36px;
    padding: 0 15px;
    -webkit-transition: color .1s, background-color .1s;
    transition: color .1s, background-color .1s
}
.globalFooterNav .optionList a:hover {
    background-color: #f6f9fc;
    color: #32325d
}
.globalFooterNav .optionList a:active {
    background-color: #e6ebf1
}
.globalFooterNav .optionList .selected {
    font-weight: 600;
    color: #32325d
}
.globalFooterNav .optionList .selected>span:before {
    content: '';
    display: inline-block;
    width: 14px;
    height: 14px;
    vertical-align: -2px;
    margin: 0 6px 0 -2px;
    background: url(/img/v3/common/footer/select/checkmark.svg)
}
.globalFooterNav .badge {
    font-size: 10px;
    line-height: 10px;
    color: #aab7c4;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .025em;
    font-style: normal;
    padding: 2px 3px 1px;
    border-radius: 4px;
    border: 1px solid #e6ebf1;
    display: inline-block;
    vertical-align: 1px;
    margin-left: 8px
}
.globalFooterNav .languagePicker {
    padding: 10px 5px
}
.globalFooterNav .countryPicker {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -webkit-flex-direction: column;
    -ms-flex-direction: column;
    flex-direction: column
}
.globalFooterNav .countryPicker:before {
    background: #f6f9fc
}
@media (min-width:670px) {
    .globalFooterNav .countryPicker {
        -webkit-box-orient: horizontal;
        -webkit-box-direction: normal;
        -webkit-flex-direction: row;
        -ms-flex-direction: row;
        flex-direction: row
    }
    .globalFooterNav .countryPicker:before {
        background: #fff
    }
}
.globalFooterNav .columns, .globalFooterNav .sidebar {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex
}
.globalFooterNav .sidebar {
    background: #f6f9fc;
    border-radius: 0 0 5px 5px;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -webkit-flex-direction: column;
    -ms-flex-direction: column;
    flex-direction: column
}
@media (min-width:670px) {
    .globalFooterNav .sidebar {
        border-radius: 0 5px 5px 0
    }
}
.globalFooterNav .sidebar .optionList a:hover {
    background-color: rgba(230, 235, 241, .5)
}
.globalFooterNav .sidebar .optionList a:active {
    background-color: rgba(207, 215, 223, .5)
}
.globalFooterNav .countryList {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -webkit-flex-direction: column;
    -ms-flex-direction: column;
    flex-direction: column;
    padding: 25px;
    -webkit-box-flex: 1;
    -webkit-flex-grow: 1;
    -ms-flex-positive: 1;
    flex-grow: 1;
    max-width: calc(100vw - 40px);
    overflow: auto;
    -webkit-overflow-scrolling: touch
}
.globalFooterNav .countryList h4 {
    margin: 0 0 5px
}
.globalFooterNav .countryList h4 a {
    line-height: 36px;
    padding: 0 15px;
    text-transform: uppercase;
    color: #6772e5;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: .025em
}
.globalFooterNav .countryList h4 a:hover {
    color: #32325d
}
.globalFooterNav .countryList h4 a:after {
    font: normal 16px StripeIcons;
    content: '\2192';
    margin-left: 6px
}
.globalFooterNav .globalLink {
    border-top: 2px solid #fff;
    padding: 30px 40px
}
.globalFooterNav .globalLink:hover {
    color: #424770
}
.globalFooterNav .globalLink:hover strong {
    color: #32325d
}
.globalFooterNav .globalLink strong {
    display: block;
    color: #6772e5;
    font-weight: 500;
    -webkit-transition: color .1s;
    transition: color .1s
}
.globalFooterNav .globalLink strong:after {
    font: normal 16px StripeIcons;
    content: '\2192';
    margin-left: 6px
}



.globalNav {
	color: #fff;
	max-width: 100%;
	margin: 0 auto;

	height: 100px;
	top:0;
	transition: all 0.3s ease-in-out;

	.dropdownRoot {
		top:100px;
		transition: all 0.3s ease-in-out;
	}

	.sec-primary-menu {
		height: 100px;
		transition: all 0.3s ease-in-out;
		
		.nav-item {
			
			height: 100%;
			display: flex;
			align-items: center;
			transition: all 0.3s ease-in-out;

			cursor: pointer;

			color: white;

			span {
				color: white;
				position: relative;
			}

			&:hover {
				color: var(--c-sky-blue);

				span {
					color: var(--c-sky-blue);
				}
			}

			// &.active {
			// 	color: var(--base-900);
			// }
		}
	}

	&.header-fixed {
		height: 80px;
		.dropdownRoot {
			top:80px;
		}

		.sec-primary-menu {
			height: 80px;

			.nav-item {
				color: var(--base-650);

				span {
					color: var(--base-650);
					transform: translateY(-4px);
				}

				&:hover {
					color: var(--c-sky-blue);

					span {
						color: var(--c-sky-blue);
					}
				}
			}
		}
	}
}



</style>
